import React from 'react';
import { AlertCircle, AlertTriangle, Link } from 'lucide-react';
import DialogNavigationWrapper from './DialogNavigationWrapper';
import FeatureHighlight from './FeatureHighlight';

const FeatureConfirmationDialog = ({
    isOpen,
    onClose,
    onConfirm,
    type,
    feature,
    message,
    dependencies = [],
    conflicts = [],
    onNavigateToFeature,
    isNavigating
}) => {
    console.log('[DEBUG] FeatureConfirmationDialog render:', {
        isOpen,
        type,
        hasFeature: !!feature,
        hasDependencies: dependencies.length > 0,
        isNavigating
    });

    if (!isOpen) return null;

    // Define dialog configurations
    const dialogConfig = {
        conflict: {
            icon: <AlertCircle className="w-6 h-6 text-red-600" />,
            title: "Feature Conflicts Detected",
            defaultMessage: "This feature cannot be set as required due to conflicts with these features:",
            showProceed: false,
            contentClass: "bg-red-50 border-red-200",
            textClass: "text-red-700",
            buttonClass: "bg-red-600 hover:bg-red-700"
        },
        dependency: {
            icon: <Link className="w-6 h-6 text-blue-600" />,
            title: "System Dependencies Required",
            defaultMessage: "Setting this feature as Required will mark the following as system dependencies:",
            showProceed: true,
            contentClass: "bg-blue-50 border-blue-200",
            textClass: "text-blue-700",
            buttonClass: "bg-blue-600 hover:bg-blue-700"
        },
        warning: {
            icon: <AlertTriangle className="w-6 h-6 text-yellow-600" />,
            title: "Feature Warning",
            defaultMessage: message || "Please review the following warning:",
            showProceed: true,
            contentClass: "bg-yellow-50 border-yellow-200",
            textClass: "text-yellow-700",
            buttonClass: "bg-yellow-600 hover:bg-yellow-700"
        }
    };

    const config = dialogConfig[type];
    if (!config) {
        console.error('[DEBUG] No config found for dialog type:', type);
        return null;
    }

    const getContentItems = () => type === 'conflict' ? conflicts : dependencies;

    const renderContent = () => {
        if (type === 'warning') {
            return (
                <div className={`rounded-lg border p-4 ${config.contentClass}`}>
                    <p className={`text-sm ${config.textClass}`}>
                        {message || config.defaultMessage}
                    </p>
                </div>
            );
        }

        return (
            <div className={`rounded-lg border p-4 ${config.contentClass}`}>
                <ul className="space-y-3">
                    {getContentItems().map((item, index) => (
                        <FeatureHighlight
                            key={item.reference || item.required_feature_reference || index}
                            featureRef={item.reference || item.required_feature_reference}
                        >
                            <li className="p-3 rounded-lg transition-colors hover:bg-white/50">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <span className="font-medium">{item.name}</span>
                                        <p className={`text-sm mt-1 ${config.textClass}`}>
                                            {item.description}
                                        </p>
                                        {type === 'conflict' && item.conflict_description && (
                                            <p className="text-sm mt-2 italic border-l-2 border-red-300 pl-2">
                                                {item.conflict_description}
                                            </p>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => onNavigateToFeature(
                                            item.reference || item.required_feature_reference,
                                            feature.reference
                                        )}
                                        className="flex items-center px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                                    >
                                        View Feature
                                    </button>
                                </div>
                            </li>
                        </FeatureHighlight>
                    ))}
                </ul>
            </div>
        );
    };

    return (
        <DialogNavigationWrapper onClose={onClose}>
            <div className="px-6 py-4 border-b flex items-center gap-3">
                {config.icon}
                <h2 className="text-xl font-semibold flex-1">{config.title}</h2>
            </div>

            <div className="p-6">
                {type !== 'warning' && (
                    <p className="text-gray-700 mb-4">
                        {message || config.defaultMessage}
                    </p>
                )}
                
                {renderContent()}

                {type === 'conflict' && (
                    <p className="text-sm text-red-600 mt-4">
                        Please resolve the conflicts before setting this feature as required.
                    </p>
                )}
            </div>

            <div className="px-6 py-4 border-t flex justify-end gap-3">
                <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                    Cancel
                </button>
                {config.showProceed && (
                    <button
                        onClick={onConfirm}
                        className={`px-4 py-2 text-sm font-medium text-white rounded-md ${config.buttonClass}`}
                    >
                        Proceed
                    </button>
                )}
            </div>
        </DialogNavigationWrapper>
    );
};

export default FeatureConfirmationDialog;